import { useState, useEffect, useCallback } from "react";

import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useSearchParams } from "react-router-dom";

import "../AuthShared.scss";
import niraLogoLong from "../../assets/NIRALogo_long.webp";
import { verifyResetToken, resetPasswordRequest } from "../../services/auth";
import { logger } from "../../utils/logger";

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const verifyToken = useCallback(async () => {
    if (!token) {
      setTokenError("No reset token provided. Please use the link from your email.");
      setVerifying(false);
      return;
    }

    try {
      const result = await verifyResetToken(token);
      if (result.valid) {
        setTokenValid(true);
      } else {
        setTokenError(result.error || "Invalid or expired reset link.");
      }
    } catch (error) {
      logger.error("Token verification error", { error, context: "ResetPassword" });
      setTokenError("Unable to verify reset link. Please try again.");
    } finally {
      setVerifying(false);
    }
  }, [token]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");

    if (password !== confirmPassword) {
      setErrorMsg("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const result = await resetPasswordRequest(token, password);
      setSuccessMsg(result.message);
    } catch (error) {
      logger.error("Reset password error", { error, context: "ResetPassword" });
      setErrorMsg(error instanceof Error ? error.message : "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (verifying) {
      return (
        <>
          <h2 className="auth-title">Verifying Link...</h2>
          <p className="auth-subtitle">Please wait while we verify your reset link.</p>
        </>
      );
    }

    if (tokenError) {
      return (
        <>
          <h2 className="auth-title">Invalid Link</h2>
          <div className="auth-message auth-message--error">{tokenError}</div>
          <Link to="/forgot-password" className="auth-back-link">
            Request a New Link
          </Link>
        </>
      );
    }

    if (successMsg) {
      return (
        <>
          <h2 className="auth-title">Password Reset</h2>
          <div className="auth-message auth-message--success">{successMsg}</div>
          <Link to="/login" className="auth-back-link">
            Go to Login
          </Link>
        </>
      );
    }

    if (tokenValid) {
      return (
        <>
          <h2 className="auth-title">Set New Password</h2>
          <p className="auth-subtitle">Enter your new password below.</p>

          {errorMsg && (
            <div className="auth-message auth-message--error">{errorMsg}</div>
          )}

          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="auth-input-group">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoFocus
              />
              <button
                type="button"
                className="show-password-btn"
                onClick={() => setShowPassword(!showPassword)}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </div>
            <div className="auth-input-group">
              <input
                type={showConfirm ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
              />
              <button
                type="button"
                className="show-password-btn"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                <FontAwesomeIcon icon={showConfirm ? faEyeSlash : faEye} />
              </button>
            </div>
            <p className="auth-password-requirements">
              Must be at least 8 characters with uppercase, lowercase, number, and special character.
            </p>
            <button
              type="submit"
              className="auth-submit-btn"
              disabled={loading || !password || !confirmPassword}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        </>
      );
    }

    return null;
  };

  return (
    <div className="auth-page-container">
      <div className="geometric-vector-wrapper">
        <div className="geometric-vector"></div>
      </div>
      <div className="geometric-vector-wrapper">
        <div className="geometric-vector-1"></div>
      </div>
      <div className="geometric-vector-wrapper">
        <div className="geometric-vector-2"></div>
      </div>
      <div className="depth-effect-overlay"></div>

      <div className="auth-card-container">
        <div className="auth-card">
          <img src={niraLogoLong} alt="NIRA Logo" className="auth-logo" />
          <div className="auth-powered-by">Powered by: Diem360</div>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
